import type { Immutable } from "immer";
import React from "react";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { t } from "ttag";

import { Context, type templates } from "@reactivated";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { Template as ChatLinkTemplate } from "../../templates/ChatLinkTemplate";

// The customer-service info-block props are a plain nested prop of the
// order-status document (AnonOrderCustomerServiceProps), not a standalone
// registered @template, so the shape is read by indexing the parent template.
type ICustomerServiceProps =
    templates.AnonOrderDocumentTemplate["customer_service"];

const useStaticURL = (): string => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const context = React.useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

const getStaticURL = (staticURL: string, path: string): string =>
    `${staticURL}${path}`;

const StaticImage = (
    props: Immutable<{ alt: string; src: string }>,
): React.JSX.Element => <img src={props.src} alt={props.alt} loading="lazy" />;

export const CustomerServiceInfoBlock = (
    props: Immutable<ICustomerServiceProps>,
): React.JSX.Element => {
    const staticURL = useStaticURL();

    return (
        <section className="customer-service-info-block">
            <header>
                <h2>{t`Need assistance? We're here to help.`}</h2>
                <div className="combined-copy">
                    {richT(
                        t`We want to hear from you, whether you have a problem, comment, or question. <chat></chat> or give us a call to speak with our Support Team today.`,
                        {
                            chat: () => (
                                <ChatLinkTemplate
                                    class_name=""
                                    chat_online_text={t`Chat`}
                                    chat_offline_text={t`Contact us`}
                                    chat_offline_link={
                                        props.contact_us_form_url
                                    }
                                    hide_if_offline={false}
                                />
                            ),
                        },
                    )}
                </div>
            </header>

            <div className="customer-service-info-block__card">
                <StaticImage
                    src={getStaticURL(staticURL, "img/icons/badge-headset.png")}
                    alt={t`headset icon`}
                />
                <h3>
                    <strong>{t`Call Us`}</strong>
                </h3>
                <p>{t`Evaluating our products and need advice before you buy?`}</p>
                <a href={props.phone_number_href}>
                    <h3>{props.phone_number_display}</h3>
                </a>
                <div className="customer-service-info-block__hours u-list-unstyled">
                    {props.support_header}
                </div>
            </div>

            <div className="customer-service-info-block__card">
                <StaticImage
                    src={getStaticURL(
                        staticURL,
                        "img/icons/badge-sleep-expert.png",
                    )}
                    alt={t`speech bubble icon`}
                />
                <h3>
                    <strong>{t`Chat with Us`}</strong>
                </h3>
                <p>{t`Already using our products or need assistance with an order you placed?`}</p>
                <ChatLinkTemplate
                    class_name="chat-link"
                    chat_online_text={t`Chat`}
                    chat_offline_text={t`Contact us`}
                    chat_offline_link={props.contact_us_form_url}
                    hide_if_offline={false}
                />
                <div className="customer-service-info-block__hours u-list-unstyled">
                    {props.chat_support_hours}
                </div>
            </div>
        </section>
    );
};
